import { Scissors, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SHAPE_LIBRARY, ICON_LIBRARY } from './constants';

export function ShapeLibrarySheet({
  open,
  onOpenChange,
  addShapeFromLibrary,
  addIconFromLibrary,
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-96">
        <SheetHeader>
          <SheetTitle>Shape & Icon Library</SheetTitle>
        </SheetHeader>
        <Tabs defaultValue="shapes" className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="shapes">Shapes</TabsTrigger>
            <TabsTrigger value="icons">Icons</TabsTrigger>
          </TabsList>
          <TabsContent value="shapes" className="mt-4">
            <ScrollArea className="h-[calc(100vh-220px)]">
              <div className="grid grid-cols-4 gap-2">
                {SHAPE_LIBRARY.map(shape => (
                  <button
                    key={shape.id}
                    onClick={() => addShapeFromLibrary(shape)}
                    className="aspect-square border border-border rounded-lg hover:border-accent hover:bg-accent/5 flex flex-col items-center justify-center p-2 transition-colors"
                    title={shape.name}
                    data-testid={`shape-${shape.id}`}
                  >
                    <span className="text-2xl">{shape.icon}</span>
                    <span className="text-[9px] text-muted-foreground mt-1 truncate w-full text-center">{shape.name}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
          <TabsContent value="icons" className="mt-4">
            <ScrollArea className="h-[calc(100vh-220px)]">
              <div className="grid grid-cols-4 gap-2">
                {ICON_LIBRARY.map(icon => (
                  <button
                    key={icon.id}
                    onClick={() => addIconFromLibrary(icon)}
                    className="aspect-square border border-border rounded-lg hover:border-accent hover:bg-accent/5 flex flex-col items-center justify-center p-2 transition-colors"
                    title={icon.name}
                    data-testid={`icon-${icon.id}`}
                  >
                    <span className="text-2xl">{icon.icon}</span>
                    <span className="text-[9px] text-muted-foreground mt-1 truncate w-full text-center">{icon.name}</span>
                  </button>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
        <div className="mt-4 pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            Click a shape or icon to add it to your mood board. Use the color pickers in the toolbar to set fill and stroke colors before adding.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ClipsPanel({
  open,
  onOpenChange,
  availableClips,
  addImageFromClip,
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-80">
        <SheetHeader>
          <SheetTitle>Add from Spec Sheets</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3 overflow-y-auto max-h-[calc(100vh-150px)]">
          {availableClips.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No clips with images found for this project.
            </p>
          ) : (
            availableClips.map(clip => (
              <div
                key={clip.id}
                className="border rounded-lg p-3 cursor-pointer hover:border-accent transition-colors"
                onClick={() => {
                  addImageFromClip(clip);
                  onOpenChange(false);
                }}
                data-testid={`clip-item-${clip.id}`}
              >
                {clip.primary_image && (
                  <img
                    src={clip.primary_image}
                    alt={clip.title}
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                )}
                <p className="font-medium text-sm truncate">{clip.title}</p>
                {clip.room && (
                  <Badge variant="secondary" className="text-xs mt-1">{clip.room}</Badge>
                )}
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function AddExternalImageDialog({
  open,
  onOpenChange,
  externalImageUrl,
  setExternalImageUrl,
  addExternalImage,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add External Image</DialogTitle>
          <DialogDescription>
            Enter the URL of an image to add to your mood board.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Image URL</Label>
            <Input
              value={externalImageUrl}
              onChange={(e) => setExternalImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              data-testid="external-image-url-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={addExternalImage} data-testid="add-external-image-btn">
            Add Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CutoutDialog({
  open,
  onOpenChange,
  cutoutTargetId,
  moodBoard,
  loadedImages,
  cutoutPoints,
  setCutoutPoints,
  applyCutout,
  cancelCutout,
}) {
  const targetEl = moodBoard?.elements?.find(el => el.id === cutoutTargetId);
  const img = loadedImages[targetEl?.image_url];

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open) cancelCutout();
    }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors className="w-5 h-5" />
            Cutout / Isolate Item
          </DialogTitle>
          <DialogDescription>
            Click to add points around the item you want to keep. Drag points to adjust. Add as many points as needed for fine detail.
          </DialogDescription>
        </DialogHeader>
        
        {/* Instructions */}
        <div className="flex gap-4 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> First point
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-[#BC5D41] inline-block" /> Other points
          </span>
          <span>• Click image to add points</span>
          <span>• Drag points to adjust</span>
          <span>• Points: <strong>{cutoutPoints.length}</strong> (min 3)</span>
        </div>
        
        <div className="relative bg-[#1A1918] rounded-lg overflow-auto" style={{ height: '55vh' }}>
          {cutoutTargetId && (() => {
            if (!targetEl || !img) {
              return (
                <div className="flex items-center justify-center h-full text-white/60">
                  <p>Loading image... If this persists, the image may not be accessible.</p>
                </div>
              );
            }
            
            // Calculate display size to fit in dialog
            const maxW = 900;
            const maxH = 500;
            const imgWidth = img.width || 300;
            const imgHeight = img.height || 300;
            const imgRatio = imgWidth / imgHeight;
            let dispW = maxW;
            let dispH = dispW / imgRatio;
            if (dispH > maxH) {
              dispH = maxH;
              dispW = dispH * imgRatio;
            }
            
            return (
              <div 
                className="relative mx-auto" 
                style={{ width: dispW, height: dispH, margin: '20px auto' }}
              >
                <img
                  src={targetEl.image_url}
                  alt="Cutout target"
                  style={{ width: dispW, height: dispH, objectFit: 'contain' }}
                  onClick={(e) => {
                    const rect = e.target.getBoundingClientRect();
                    const relX = (e.clientX - rect.left) / rect.width;
                    const relY = (e.clientY - rect.top) / rect.height;
                    setCutoutPoints(prev => [...prev, { x: relX, y: relY }]);
                  }}
                  className="cursor-crosshair select-none"
                  draggable={false}
                />
                {/* Draw polygon and points - SVG overlay */}
                <svg
                  style={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    width: '100%', 
                    height: '100%',
                    overflow: 'visible',
                    pointerEvents: 'none'
                  }}
                  viewBox="0 0 1 1"
                  preserveAspectRatio="none"
                >
                  {/* Polygon fill preview */}
                  {cutoutPoints.length >= 3 && (
                    <polygon
                      points={cutoutPoints.map(p => `${p.x},${p.y}`).join(' ')}
                      fill="rgba(188, 93, 65, 0.25)"
                      stroke="#BC5D41"
                      strokeWidth="0.003"
                      strokeDasharray="0.01 0.005"
                    />
                  )}
                  {/* Lines connecting points */}
                  {cutoutPoints.length >= 2 && (
                    <polyline
                      points={cutoutPoints.map(p => `${p.x},${p.y}`).join(' ')}
                      fill="none"
                      stroke="#BC5D41"
                      strokeWidth="0.004"
                    />
                  )}
                  {/* Closing line preview (dotted) */}
                  {cutoutPoints.length >= 3 && (
                    <line
                      x1={cutoutPoints[cutoutPoints.length - 1].x}
                      y1={cutoutPoints[cutoutPoints.length - 1].y}
                      x2={cutoutPoints[0].x}
                      y2={cutoutPoints[0].y}
                      stroke="#BC5D41"
                      strokeWidth="0.003"
                      strokeDasharray="0.01 0.005"
                      opacity="0.5"
                    />
                  )}
                </svg>
                
                {/* Draggable points - rendered as divs for better drag handling */}
                {cutoutPoints.map((p, i) => (
                  <div
                    key={i}
                    className="absolute cursor-move"
                    style={{
                      left: `${p.x * 100}%`,
                      top: `${p.y * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      backgroundColor: i === 0 ? '#22c55e' : '#BC5D41',
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 10
                    }}
                    draggable={false}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const imgRect = e.target.parentElement.querySelector('img').getBoundingClientRect();
                      
                      const handleMouseMove = (moveE) => {
                        const relX = Math.max(0, Math.min(1, (moveE.clientX - imgRect.left) / imgRect.width));
                        const relY = Math.max(0, Math.min(1, (moveE.clientY - imgRect.top) / imgRect.height));
                        setCutoutPoints(prev => prev.map((pt, idx) => 
                          idx === i ? { x: relX, y: relY } : pt
                        ));
                      };
                      
                      const handleMouseUp = () => {
                        document.removeEventListener('mousemove', handleMouseMove);
                        document.removeEventListener('mouseup', handleMouseUp);
                      };
                      
                      document.addEventListener('mousemove', handleMouseMove);
                      document.addEventListener('mouseup', handleMouseUp);
                    }}
                    title={`Point ${i + 1}${i === 0 ? ' (start)' : ''} - drag to adjust`}
                  >
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-white bg-black/50 px-1 rounded">
                      {i + 1}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
        
        <DialogFooter className="flex-wrap gap-2">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setCutoutPoints([])}>
              Clear All
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCutoutPoints(prev => prev.slice(0, -1))}
              disabled={cutoutPoints.length === 0}
            >
              Undo Last
            </Button>
          </div>
          <div className="flex-1" />
          <Button variant="outline" onClick={cancelCutout}>
            Cancel
          </Button>
          <Button onClick={applyCutout} disabled={cutoutPoints.length < 3} data-testid="apply-cutout-btn">
            <Check className="w-4 h-4 mr-2" />
            Apply Cutout ({cutoutPoints.length} points)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
